import Footer from "./Footer";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaCalendarAlt,
  FaClock,
  FaGift,
  FaLongArrowAltRight,
  FaUserMd,
  FaChalkboardTeacher,
} from "react-icons/fa";
import { RxCrossCircled } from "react-icons/rx";

import Afterbefore from "../asset/landingpage/afterbefore.webp";
import revibe from "../asset/landingpage/Revibe.webp";

import image1 from "../asset/testinomial/Artboard1.webp";
import image2 from "../asset/testinomial/Artboard2.webp";
import image3 from "../asset/testinomial/Artboard3.webp";
import image4 from "../asset/testinomial/Artboard4.webp";
import image5 from "../asset/testinomial/Artboard5.webp";
import { Brain, Utensils, Scale } from "lucide-react";
import hormonal from "../asset/hormon.png";
import Metabolism from "../asset/Metabolism.png";

const testimonials = [
  { id: 1, img: image1 },
  { id: 2, img: image2 },
  { id: 3, img: image3 },
  { id: 4, img: image4 },
  { id: 5, img: image5 },
];

const tickerItems = [
  "Sustainable Weight-loss",
  "Restore Hormonal Balance",
  "Reset Metabolism",
  "No Crash Diet",
  "No Gym & Trainer",
  "No Herbalife",
];

const rootCauses = [
  {
    text: "Stress, Overthinking & Depression",
    icon: Brain,
  },
  {
    text: "Over Eating",
    icon: Utensils,
  },
  {
    text: "Metabolism",
    img: Metabolism,
  },
  {
    text: "Crash Diets",
    icon: Scale,
  },
  {
    text: "Hormonal Imbalances & Deficiencies",
    img: hormonal,
  },
];
const cardColors = [
  "bg-green-50 border-green-200 text-green-800",
  "bg-yellow-50 border-yellow-200 text-yellow-800",
  "bg-orange-50 border-orange-200 text-orange-800",
  "bg-blue-50 border-blue-200 text-blue-800",
  "bg-purple-50 border-purple-200 text-purple-800",
];

const keyFeatures = [
  "NO Crash or Fancy Diet",
  "NO Gym or Trainer Fees",
  "NO Shakes, Juices, or Hunger Pains",
  "NO Ozempic, Mounjaro",
];

const attendList = [
  "Obese between 15 and 75 age",
  "Diabetes, Thyroid, PCOD, PCOS, BP, Fatty Liver Patients",
  "Post-Pregnancy Weight Gain",
  "Menopause Weight Gain",
  "Hormonal Imbalances & Poor Metabolism",
];

// Common Register Button Component
const RegisterButton = ({ className = "", children }) => {
  const navigate = useNavigate();

  const handleRegisterClick = () => {
    navigate("/register-form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <button
      onClick={handleRegisterClick}
      className={`bg-[#E80003] hover:bg-[#d31111] font-bold text-white rounded-xl px-8 py-4 text-sm md:text-base uppercase tracking-wide shadow-lg hover:shadow-xl transition-all duration-300 animate-blink ${className}`}
      style={{ animation: "blink 1.5s infinite" }}
    >
      {children || "Yes! I Want To Register"}
    </button>
  );
};

const LandingPage = () => {
  const [workshopData, setWorkshopData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWorkshopData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/workshops`,
        );
        if (response?.data?.success && response?.data?.data?.length > 0) {
          setWorkshopData(response.data.data[0]);
        }
      } catch (error) {
        console.error("Error fetching workshop data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkshopData();
  }, []);

  const infoBoxes = [
    {
      icon: <FaCalendarAlt style={{ fontSize: 26, color: "#1B4332" }} />,
      text: workshopData?.date || "Coming Soon",
    },
    {
      icon: <FaClock style={{ fontSize: 26, color: "#1B4332" }} />,
      text: workshopData?.time || "7:00 PM IST",
    },
  ];

  const CTABox = ({ green = false }) => (
    <div className="bg-white">
      <div className="grid grid-cols-2 gap-3 mb-6">
        {infoBoxes.map((box, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-xl p-3"
          >
            <div className="flex-shrink-0">{box.icon}</div>
            <span className="text-green-900 font-bold text-sm leading-tight">
              {box.text}
            </span>
          </div>
        ))}
      </div>

      <RegisterButton className="w-full text-xl md:text-2xl py-4" />
    </div>
  );

  const GuaranteeBox = ({ text }) => (
    <div
      className="bg-gradient-to-r from-yellow-100 to-amber-100 border-4 border-yellow-400 rounded-3xl p-5 text-center animate-blink"
      style={{
        animation: "blink 1.5s infinite",
      }}
    >
      <p className="text-sm md:text-xl font-extrabold uppercase text-orange-900">
        {text}
      </p>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');

        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }

        .marquee-track {
          animation: marquee 22s linear infinite;
        }
      `}</style>

      {/* Top Ticker */}
      <div className="bg-green-900 py-3 overflow-hidden">
        <div className="whitespace-nowrap">
          <div className="marquee-track inline-flex">
            {[...tickerItems, ...tickerItems].map((item, index) => (
              <span
                key={index}
                className="px-6 md:px-10 text-white font-semibold text-xs md:text-sm uppercase tracking-wide"
              >
                ✦ {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Subheader */}
      <div className="bg-gradient-to-r from-orange-600 to-amber-500 text-center px-4 py-5 md:py-7">
        <p
          className="text-white font-black uppercase tracking-wide leading-tight
                text-4xl md:text-xl max-w-5xl mx-auto"
        >
          ASSURED, SUSTAINABLE & India’s Most Affordable Weight-Loss Program
        </p>
      </div>

      {/* Section 1 - Hero */}
      <section className="bg-gradient-to-b from-white to-gray-50 grid lg:grid-cols-2 gap-8 items-center px-8 py-8 md:py-10">
        {/* LEFT CONTENT */}
        <div>
          <h1 className=" text-2xl lg:text-5xl  font-black leading-tight mb-4 text-gray-900">
            Meet Your <br />
            <span className="text-transparent lg:text-5xl bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500">
              Transformation Coach
            </span>
          </h1>

          <p className="text-base md:text-xl  font-semibold text-green-800 mb-4">
            This will be the Most Interesting, Beneficial, Unique & Transforming
            90 Mins Zoom Masterclass
          </p>

          {/* MOBILE IMAGE */}
          <div className="relative mb-4 lg:hidden">
            <img
              src={Afterbefore}
              alt="Transformation Coach"
              className="w-full h-[260px] object-cover rounded-3xl shadow-xl"
            />
          </div>

          {/* INFO BOXES */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {infoBoxes.map((box, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 bg-gradient-to-r from-yellow-100 to-amber-100 rounded-xl p-3 shadow-sm"
              >
                <div className="flex-shrink-0 text-orange-600">{box.icon}</div>

                <span className="text-green-900 font-bold text-sm leading-tight">
                  {box.text}
                </span>
              </div>
            ))}

            {/* DESKTOP CTA */}
            <div className="col-span-2 hidden lg:block">
              <RegisterButton className="w-full" />
            </div>
          </div>

          {/* MOBILE CTA (NOW BELOW BOXES) */}
          <div className="mb-6 lg:hidden">
            <RegisterButton className="w-full" />
          </div>
        </div>

        {/* DESKTOP IMAGE */}
        <div className="relative hidden lg:block">
          <img
            src={Afterbefore}
            alt="Transformation Coach"
            className="w-full h-[300px] md:h-[430px] object-cover rounded-3xl shadow-xl"
          />
        </div>
      </section>

      {/* Section 2 - Journey + CTA */}
      <section className="grid lg:grid-cols-2 gap-8 items-center px-8 py-8 md:py-10">
        {/* LEFT SIDE IMAGE */}
        <div className="rounded-xl overflow-hidden shadow-xl">
          <img
            src={revibe}
            alt="India Builds Fitness Journey"
            className="w-full h-[500px] md:h-[360px] lg:h-[420px] "
          />
        </div>

        {/* RIGHT SIDE CONTENT */}
        <div className="text-gray-900">
          <CTABox />
        </div>
      </section>

      {/* Section 3 - Root Causes */}
      <section className="bg-gradient-to-b from-white to-gray-50 md:p-10 px-8 py-8 md:py-10">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-5xl font-black text-gray-900">
            Root Causes of Obesity & Assured Solutions Given in the Masterclass
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {rootCauses.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={index}
                className={`${cardColors[index]} border rounded-2xl p-5`}
              >
                <div className="flex items-start gap-3">
                  {Icon ? (
                    <Icon className="text-xl mt-1 shrink-0" />
                  ) : (
                    <img
                      src={item.img}
                      alt={item.text}
                      className="w-6 h-6 mt-1 object-contain shrink-0"
                    />
                  )}

                  <p className="font-semibold">{item.text}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Section 4 - Guarantee */}
      <section className="px-8 py-8 md:py-10">
        <GuaranteeBox text="NO FAKE HYPE - 100% TRANSFORMATION or 100% Money Back Guarantee" />
      </section>

      {/* Section 5 - CTA */}
      <section className="px-8 py-8 md:py-10">
        <CTABox green />
      </section>

      {/* Section 6 - Key Features */}
      <section className="bg-green-900 rounded-3xl p-6 md:p-10 text-white mx-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-5xl font-black">
            Our Key Features In Weight Loss
          </h2>
          <p className="mt-3 text-yellow-300 text-2xl md:text-3xl font-bold">
            By Ancient Secrets of Ayurveda
          </p>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {keyFeatures.map((item, index) => (
            <div
              key={index}
              className="bg-green-800/50 border border-green-700 rounded-2xl p-5 flex items-start gap-4"
            >
              <RxCrossCircled className="text-yellow-300 text-2xl mt-1 shrink-0" />
              <p className="font-semibold text-base md:text-lg text-white">
                {item}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 7 - CTA */}
      <section className="px-8 py-8 md:py-10">
        <CTABox />
      </section>

      {/* Section 8 - Guarantee */}
      <section className="px-8 py-8 md:py-10">
        <GuaranteeBox text="NO FAKE HYPE - Transformation or 100% Money Back Guarantee" />
      </section>

      {/* Section 9 - Who Should Attend */}
      <section className="p-6 md:p-10 bg-gradient-to-b from-white to-gray-50 mx-8 rounded-3xl">
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-5xl font-black text-gray-900">
            Who Should Attend this Masterclass
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {attendList.map((item, index) => (
            <div
              key={index}
              className={`${cardColors[index % cardColors.length]} border rounded-2xl p-5 flex gap-4 items-start shadow-sm`}
            >
              <FaUserMd className="text-orange-600 text-2xl mt-1 shrink-0" />
              <p className="font-semibold text-gray-800">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Section 10 - CTA */}
      <section className="px-8 py-8 md:py-10">
        <CTABox green />
      </section>

      {/* Section 11 - Testimonials */}
      <section className="bg-green-900 rounded-3xl p-4 md:p-8 text-white mx-8">
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-2xl md:text-5xl font-black">Testimonials</h2>
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 md:gap-5">
          {testimonials.map((item) => (
            <div
              key={item.id}
              className="group relative overflow-hidden shadow-lg bg-black"
            >
              <img
                src={item.img}
                alt={`Testimonial ${item.id}`}
                className="w-full aspect-[3/4] object-cover transition duration-500"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Section 12 - Guarantee */}
      <section className="px-8 py-8 md:py-10">
        <GuaranteeBox text="NO FAKE HYPE - Transformation or 100% Money Back Guarantee" />
      </section>

      {/* Section 13 - CTA */}
      <section className="px-8 py-8 md:py-10">
        <CTABox />
      </section>

      {/* Section 14 - Final CTA */}
      <section className="bg-green-900 rounded-3xl p-8 text-center text-white mx-8 mb-8">
        <p className="text-lg sm:text-xl md:text-2xl font-normal text-yellow-300 mb-4 leading-relaxed px-2">
          We have helped IBS, CKT, and Heart Patients to Lose Weight
          Successfully.
        </p>

        <h2 className="text-lg sm:text-xl md:text-3xl font-normal mb-8 leading-relaxed px-2">
          See You in the Most Interesting, Beneficial, Unique & Transforming 90
          Mins Zoom Masterclass
        </h2>

        <div className="max-w-5xl mx-auto">
          <RegisterButton className="w-full font-extrabold" />
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default LandingPage;
